/**
 * Blocks direct navigation to multi-step flow result pages.
 * Results are only reachable from input pages (location.state / in-flow session)
 * or future history replay (fromHistory flag).
 */

import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { LENS_SESSION_KEY, LENS_SESSION_TTL_MS, LENS_V6_SESSION_KEY } from '@/types/lens-api'

const ANALYZER_SESSION_KEY = 'analyzer_session_state'

function hasAnalyzerFlowSession(): boolean {
  try {
    const raw = sessionStorage.getItem(ANALYZER_SESSION_KEY)
    if (!raw) return false
    const parsed = JSON.parse(raw) as {
      currentStep?: number
      analysisResult?: unknown
      exploreData?: unknown
    }
    return (
      (parsed.currentStep === 2 || parsed.currentStep === 3) &&
      Boolean(parsed.analysisResult || parsed.exploreData)
    )
  } catch {
    return false
  }
}

function hasLensFlowSession(): boolean {
  for (const key of [LENS_SESSION_KEY, LENS_V6_SESSION_KEY]) {
    try {
      const raw = sessionStorage.getItem(key)
      if (!raw) continue
      const parsed = JSON.parse(raw) as {
        timestamp?: number
        result?: unknown
        pendingV7Pipeline?: boolean
        analyzeInput?: unknown
        assessmentId?: string
      }
      if (!parsed.timestamp || Date.now() - parsed.timestamp > LENS_SESSION_TTL_MS) continue
      if (parsed.result) return true
      if (parsed.pendingV7Pipeline && parsed.assessmentId) return true
    } catch {
      // try next key
    }
  }
  return false
}

interface FlowResultsGuardProps {
  flow: 'analyzer' | 'lens'
  children: ReactNode
}

export function FlowResultsGuard({ flow, children }: FlowResultsGuardProps) {
  const location = useLocation()
  const state = location.state as Record<string, unknown> | null
  const assessmentId = new URLSearchParams(location.search).get('assessmentId')

  if (state?.fromHistory === true) {
    return <>{children}</>
  }

  if (flow === 'analyzer') {
    const hasState = Boolean(state?.analysisResult || state?.exploreData)
    if (hasState || hasAnalyzerFlowSession()) {
      return <>{children}</>
    }
    return <Navigate to="/analyze" replace />
  }

  // Allow completed results and pending v7 pipeline handoffs.
  if (assessmentId) {
    return <>{children}</>
  }
  const hasState = Boolean(
    state?.result ||
    state?.v6Result ||
    state?.pendingAnalyze ||
    state?.pendingV7AssessmentId ||
    state?.linaRunId,
  )
  if (hasState || hasLensFlowSession()) {
    return <>{children}</>
  }
  return <Navigate to="/lens" replace />
}
